import { useDataPart, useStepContext } from '@/app/core/components/step-wizard';
import { useProgramDependentDataContext } from '@/app/core/components/program-wizard/program-dependent-data-context';
import { useEffect, useCallback } from 'react';
import * as yup from 'yup';
import { FieldName, FieldValues } from 'react-hook-form/dist/types/form';

interface ProgramWizardStepDataProcessorProps {
  stepKey: string[];
  triggerFieldNames: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validator: yup.ObjectSchema<any> | yup.ArraySchema<any>;
  trigger(
    name?: FieldName<FieldValues> | FieldName<FieldValues>[],
  ): Promise<boolean>;
  model: unknown;
  isDirty: boolean;
  validatorContext?: yup.ValidateOptions | undefined;
}

export const ProgramWizardStepDataProcessor = (
  props: ProgramWizardStepDataProcessorProps,
) => {
  const {
    stepKey,
    triggerFieldNames,
    validator,
    trigger,
    model,
    isDirty,
    validatorContext,
  } = props;
  const { onStepDeactivate, isStepActive } = useStepContext();

  const { forceSubmitState } = useProgramDependentDataContext();
  const [_dataPartState, setDataPartState] = useDataPart(stepKey);

  onStepDeactivate(() => {
    return new Promise((resolve, _reject) => {
      trigger(triggerFieldNames);
      resolve(true);
    });
  });

  const updateData = useCallback(
    (isValidForm: boolean) => {
      if (model) {
        setDataPartState((currentState) => {
          const newState = {
            ...currentState,
            isValid: isValidForm,
            isDirty: isDirty,
            data: model instanceof Array ? [...model] : { ...model },
          };
          return newState;
        });
      }
    },
    [model, setDataPartState, isDirty],
  );

  const updateFormData = useCallback(() => {
    if (model && validator) {
      validator
        .validate(model, validatorContext)
        .then(() => {
          updateData(true);
        })
        .catch((_error) => {
          updateData(false);
        });
    }
  }, [model, validator, updateData, validatorContext]);

  useEffect(() => {
    if (model && validator) {
      updateFormData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, validator]);

  useEffect(() => {
    if (forceSubmitState !== 'Init') {
      updateFormData();
      if (isStepActive) {
        trigger(triggerFieldNames);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceSubmitState]);

  return <></>;
};
